import React from "react"
import { useContext, useRef, useState } from "react"
import { KeyFeaturesModuleProps } from "./KeyFeaturesModuleProps"
import { DisclaimersContext } from "../../../../contexts/Disclaimers"
import tw from "twin.macro"
import { parseDisclaimerBlocks } from "../../../../helpers"
import { toggleDisclaimersModal } from "../../../../contexts/Disclaimers/actions"
import Icon from "../../../atoms/Icon"
import { Image } from "../../../atoms/Image"
import ImageModal from "../StackedImageModule/ImageModal"
import { imageWithAlt } from "../../../../global"
import AliceCarousel from "react-alice-carousel"
import useDrag from "../../../../hooks/useDrag"
import { TabProps, Tabs } from "../../../atoms/Tabs"
import FeaturesCategoryFilter from "./FeaturesCategoryFilter"
import useMediaQuery from "../../../../hooks/useMediaQuery"

const KeyFeaturesModule: React.FC<KeyFeaturesModuleProps> = ({
  title,
  description,
  featuredImage,
  featuredItems,
  featuredItemsWithIcon,
  featuredItemsWithImage,
  featuredItemsCategories,
  cardLayout,
  layout,
}) => {
  const [disclaimerState, dispatch] = useContext(DisclaimersContext)
  const [modalOpen, setModalOpen] = useState(false)
  const [expandedImage, setExpandedImage] = useState<imageWithAlt | null>(
    featuredImage || null,
  )
  const [expandedImageIndex, setExpandedImageIndex] = useState<number | null>(
    null,
  )
  const [activeTab, setActiveTab] = useState(0)
  const parentContainer = useRef<HTMLDivElement>(null)
  const [showFilter, setShowFilter] = useState(false)

  const isDesktop = useMediaQuery("(min-width: 1024px)")

  const getDisclaimerParentPosition = (): {
    left: number | null
    right: number | null
  } => {
    if (parentContainer.current) {
      const parentContainerRect =
        parentContainer.current.getBoundingClientRect()
      return {
        left: parentContainerRect?.left - 18,
        right: parentContainerRect?.right - 18,
      }
    } else {
      return {
        left: null,
        right: null,
      }
    }
  }
  const disclaimerParentPosition = getDisclaimerParentPosition()

  const getLayoutType = () => {
    // MULTIPLE CATEGORIES (Tabbed)
    if (
      layout === "multipleCategories" &&
      featuredItemsCategories &&
      featuredItemsCategories[activeTab]
    ) {
      const category = featuredItemsCategories[activeTab]
      if (
        category.layout === "textOnly" &&
        category.featuredItems?.length > 0
      ) {
        return "tabTextOnly"
      }
      if (
        category.layout === "withIcon" &&
        category.featuredItemsWithIcon?.length > 0
      ) {
        return "tabWithIcon"
      }
      if (
        category.layout === "withImage" &&
        category.featuredItemsWithImage?.length > 0
      ) {
        return "tabWithImage"
      }
    }

    // NO CATEGORIES (No tabs)
    if (layout === "noCategories") {
      if (cardLayout === "textOnly" && featuredItems?.length > 0) {
        return "noTabTextOnly"
      }
      if (cardLayout === "withIcon" && featuredItemsWithIcon?.length > 0) {
        return "noTabWithIcon"
      }
      if (cardLayout === "withImage" && featuredItemsWithImage?.length > 0) {
        return "noTabWithImage"
      }
    }

    return null
  }

  const layoutType = getLayoutType()

  let itemsToRender: any[] = []

  switch (layoutType) {
    case "tabTextOnly":
      itemsToRender = featuredItemsCategories[activeTab].featuredItems
      break
    case "tabWithIcon":
      itemsToRender = featuredItemsCategories[activeTab].featuredItemsWithIcon
      break
    case "tabWithImage":
      itemsToRender = featuredItemsCategories[activeTab].featuredItemsWithImage
      break
    case "noTabTextOnly":
      itemsToRender = featuredItems
      break
    case "noTabWithIcon":
      itemsToRender = featuredItemsWithIcon
      break
    case "noTabWithImage":
      itemsToRender = featuredItemsWithImage
      break
  }

  if (!layoutType || itemsToRender.length === 0) {
    return null
  }

  const items = itemsToRender.map((item, index) => {
    return (
      <div
        css={[
          tw`w-auto mx-3 h-auto bg-[#E8E4E0] px-10 pt-16 pb-16 rounded-2xl flex flex-col gap-6 min-h-[600px] md:(min-h-[520px]) lg:(min-h-[450px])`,
          item?.fullImage?.image && tw`flex pt-0 px-0`,
          index === 0 && tw`ml-0`,
        ]}
        key={item._key}
      >
        {item?.icon?.image && (
          <Image
            imageData={item?.icon?.image}
            css={[tw`h-10 ml-0 mr-auto`]}
            alt={item.icon?.image?.asset?.altText}
          />
        )}
        {item?.fullImage?.image && (
          <div css={tw`relative w-full h-0 pb-[56.25%]`}>
            <Image
              imageData={item?.fullImage?.image}
              alt={item.fullImage?.image?.asset?.altText}
              css={[
                tw`absolute top-0 left-0 w-full h-full object-cover rounded-t-2xl`,
              ]}
            />
          </div>
        )}
        {item.title && (
          <p
            css={[
              tw`text-left text-3xl leading-10!`,
              item?.fullImage?.image && tw`px-10!`,
            ]}
          >
            {parseDisclaimerBlocks(
              item.title,
              selection => dispatch(toggleDisclaimersModal(selection)),
              disclaimerParentPosition.left!,
              disclaimerParentPosition.right!,
              200,
            )}
          </p>
        )}
        {item.description && (
          <p
            css={[
              tw`text-left text-xl font-light leading-9`,
              item?.fullImage?.image && tw`px-10!`,
            ]}
          >
            {parseDisclaimerBlocks(
              item.description,
              selection => dispatch(toggleDisclaimersModal(selection)),
              disclaimerParentPosition.left!,
              disclaimerParentPosition.right!,
              200,
            )}
          </p>
        )}
      </div>
    )
  })

  function handleClose() {
    setModalOpen(!modalOpen)
    document.body.style.overflow = "auto"
  }

  const responsive = {
    0: { items: 1 },
    600: { items: 2 },
    1024: { items: 3 },
  }
  const renderPrevButton = ({ isDisabled }: { isDisabled?: boolean }) => {
    return (
      <button
        css={[
          tw`absolute -left-8 top-[45%] -translate-y-1/2 z-50 flex justify-center items-center cursor-pointer w-12 h-8 bg-gunmetal rounded-full transition-all hover:bg-gray-400 focus-visible:(bg-gray-400)`,
          tw`lg:(-left-6)`,
          isDisabled ? tw`hidden` : tw`flex`,
        ]}
      >
        <Icon.Chevron direction="left" color="white" css={[tw`h-4`]} />
      </button>
    )
  }

  const renderNextButton = ({ isDisabled }: { isDisabled?: boolean }) => {
    return (
      <button
        css={[
          tw`absolute -right-8 top-[45%] -translate-y-1/2 z-50 flex justify-center items-center cursor-pointer w-12 h-8 bg-gunmetal rounded-full transition-all hover:(bg-gray-400) focus-visible:(bg-gray-400)`,
          tw`lg:(-right-1)`,
          isDisabled ? tw`hidden` : tw`flex`,
        ]}
      >
        <Icon.Chevron direction="right" color="white" css={[tw`h-4`]} />
      </button>
    )
  }

  return (
    <section css={[tw` max-w-[1440px] px-10 py-32 mx-auto`]}>
      {(title || description) && (
        <div css={[tw`flex flex-col items-center gap-8 text-center mb-16`]}>
          {title && (
            <h2
              css={[
                tw`text-5xl block leading-[104.6%] lg:text-5xl xl:text-5xl`,
              ]}
            >
              {parseDisclaimerBlocks(title, selection =>
                dispatch(toggleDisclaimersModal(selection)),
              )}
            </h2>
          )}
          {description && (
            <p css={[tw`max-w-screen-sm text-lg`]}>
              {parseDisclaimerBlocks(description, selection =>
                dispatch(toggleDisclaimersModal(selection)),
              )}
            </p>
          )}
        </div>
      )}

      {featuredImage?.image && (
        <div css={[tw`flex`]}>
          <div
            css={[
              tw`relative w-full h-[220px] md:h-[400px] lg:h-[700px]`,
              featuredItemsCategories?.length < 1 && tw`mb-16 lg:mb-28`,
            ]}
          >
            <Image
              imageData={featuredImage?.image}
              css={[
                tw`object-cover rounded-2xl`,
                tw`w-full h-[220px] md:h-[400px] lg:h-[700px]`,
              ]}
            />
            <button
              onClick={() => {
                setModalOpen(true)
              }}
              css={[
                tw`absolute z-[49] top-4 right-4 bg-gray-100 rounded-full shadow-2 p-2 px-2! opacity-100 hover:opacity-80`,
              ]}
            >
              <Icon.Expand color={"black"} css={[tw`h-6 w-6`]} />
            </button>
          </div>
        </div>
      )}

      <div ref={parentContainer}>
        {/* MOBILE VIEW */}
        <>
          {!isDesktop && featuredItemsCategories?.length > 1 && (
            <FeaturesCategoryFilter
              categories={featuredItemsCategories}
              selectedCategory={activeTab}
              showFilter={showFilter}
              setShowFilter={setShowFilter}
              handleSelectChange={setActiveTab}
            />
          )}
          {!isDesktop && items && itemsToRender.length > 0 && (
            <AliceCarousel
              mouseTracking
              items={items}
              disableDotsControls
              paddingLeft={0}
              paddingRight={0}
              responsive={responsive}
              controlsStrategy="alternate"
              renderPrevButton={renderPrevButton}
              renderNextButton={renderNextButton}
            />
          )}
        </>

        {/* DESKTOP VIEW */}
        <div>
          {isDesktop && featuredItemsCategories?.length > 1 && (
            <Tabs
              families={featuredItemsCategories}
              activeFamily={activeTab}
              setActiveFamily={setActiveTab}
              css={[tw`bg-none text-xl z-[100] mb-8`]}
            >
              {featuredItemsCategories.map(category => (
                <Tab key={category._key} title={category.title} />
              ))}
            </Tabs>
          )}

          {isDesktop && items && itemsToRender.length > 0 && (
            <AliceCarousel
              mouseTracking
              items={items}
              disableDotsControls
              paddingLeft={0}
              paddingRight={0}
              responsive={responsive}
              controlsStrategy="alternate"
              renderPrevButton={renderPrevButton}
              renderNextButton={renderNextButton}
            />
          )}
        </div>
      </div>

      {/* IMAGE MODAL */}
      {modalOpen && (
        <ImageModal
          expandedImage={expandedImage}
          expandedImageIndex={0}
          setExpandedImage={setExpandedImage}
          setExpandedImageIndex={setExpandedImageIndex}
          slides={[featuredImage]}
          open={modalOpen}
          onClose={handleClose}
        />
      )}
    </section>
  )
}
export default KeyFeaturesModule

/* TAB CONTENT */
const Tab: React.FC<TabProps> = ({ children }) => {
  const { handleMouseUp } = useDrag()

  if (!children) return null

  return (
    <div onMouseUp={handleMouseUp} css={[tw`mt-10`]}>
      <div css={[tw`h-[510px]`]}>
        <div
          css={[tw`flex flex-nowrap h-full w-full text-center justify-center`]}
        >
          {children}
        </div>
      </div>
    </div>
  )
}
